// billing-page.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Row,
  Skeleton,
  Space,
  Switch,
  Typography,
  Tag,
  Divider,
  Badge,
  Flex,
  Tabs,
} from 'antd';
import {
  CheckOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  StarOutlined,
  FireOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import Faq from './faq';
import { useStore } from '../../helpers/use-store';
import {
  BillingCycle,
  PaymentProvider,
  Plan,
  SubscriptionStatusName,
} from '@monorepo/types';
import { orderBy } from 'lodash';
import { observer } from 'mobx-react-lite';
import SubscriptionInfo from './subscription-info';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { checkoutStyles } from './biiling-custom-css';
import PlanChangeModal, {
  PlanChangeModalActions,
} from './billing-modals/plan-change-modal';
import CouponDisplay from './coupon';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import './biliing-page.scss';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

// Dynamic plan styling based on name or index
const getPlanStyle = (planName: string, index: number, totalPlans: number) => {
  const colors = [
    '#1890ff', // Blue
    '#722ed1', // Purple
    '#eb2f96', // Pink
    '#faad14', // Gold
    '#52c41a', // Green
  ];

  const icons = [
    <RocketOutlined />,
    <ThunderboltOutlined />,
    <StarOutlined />,
    <CrownOutlined />,
    <FireOutlined />,
  ];

  // Try to match common plan names
  const nameLower = planName.toLowerCase();
  if (nameLower.includes('starter') || nameLower.includes('basic')) {
    return { color: colors[0], icon: icons[0] };
  }
  if (nameLower.includes('pro') || nameLower.includes('professional')) {
    return { color: colors[1], icon: icons[1] };
  }
  if (nameLower.includes('creator') || nameLower.includes('premium')) {
    return { color: colors[2], icon: icons[2] };
  }
  if (nameLower.includes('influencer') || nameLower.includes('enterprise')) {
    return { color: colors[3], icon: icons[3] };
  }

  // Fallback to index-based color/icon
  return {
    color: colors[index % colors.length],
    icon: icons[index % icons.length],
  };
};

// Determine which plan should be marked as "popular"
const getPopularPlanIndex = (plans: Plan[]) => {
  // If there's a "Creator" or middle-priced plan, mark it as popular
  const creatorIndex = plans.findIndex((p) =>
    p.name.toLowerCase().includes('creator')
  );
  if (creatorIndex !== -1) return creatorIndex;

  // Otherwise, mark the middle plan as popular (for 3+ plans)
  if (plans.length >= 3) {
    return Math.floor(plans.length / 2);
  }

  return -1; // No popular plan
};

export const BillingPage = () => {
  const { t } = useTranslation();
  const {
    dataStore: { userStore, planStore },
  } = useStore();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(
    BillingCycle.Monthly
  );
  const { loadingState, updateLoadingState } = useLoading();
  const navigate = useNavigate();
  const planChangeModalRef = useRef<PlanChangeModalActions>(null);
  const previousSubscriptionRef = useRef<{
    isActive: boolean;
    planId?: string;
    topupCredits: number;
  }>({
    isActive: false,
    planId: undefined,
    topupCredits: 0,
  });
  const purchasedPlanRef = useRef<Plan | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        updateLoadingState(LoadingState.Loading);
        planStore.fetchAll();

        document.body.scrollIntoView({
          behavior: 'smooth',
        });
      } catch (e) {
        console.error(`failed loading plans`, e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, []);

  // Filter subscription plans (Monthly/Annual)
  const subscriptionPlans = orderBy(
    planStore.items.filter((item) => item.billingCycle === billingCycle),
    'price',
    'asc'
  );

  // Filter topup credit packages (ONE_TIME)
  const topupPlans = orderBy(
    planStore.items.filter(
      (item) => item.billingCycle === BillingCycle.OneTime
    ),
    'price',
    'asc'
  );

  const popularPlanIndex = getPopularPlanIndex(subscriptionPlans);

  const showCheckout = useCallback(
    async (event: React.MouseEvent, plan: Plan) => {
      event.preventDefault();

      // Store the plan being purchased
      purchasedPlanRef.current = plan;

      previousSubscriptionRef.current = {
        isActive: userStore.isSubscriptionActive,
        planId: userStore.userSubscription.planId,
        topupCredits: userStore.userSubscription.topupCredits || 0,
      };

      let session: { url: string; provider: string };
      try {
        session = await userStore.createCheckoutSession(plan._id);
      } catch (e) {
        console.error('failed creating checkout session', e);
        return;
      }

      if (session.provider === PaymentProvider.Polar) {
        window.location.href = session.url;
        return;
      }

      // PayPro: render iframe modal
      const body = document.getElementsByTagName('body')[0];

      const modalHtml = `
      <div class="ppg-checkout-modal ppg-show" id="ppg-checkout-modal">
        <div class="ppg-btn-close" id="ppg-btn-close">
          <img src="data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiBlbmFibGUtYmFja2dyb3VuZD0ibmV3IDAgMCA1MTIgNTEyIj48cGF0aCBkPSJNNDQzLjYgMzg3LjEgMzEyLjQgMjU1LjRsMTMxLjUtMTMwYzUuNC01LjQgNS40LTE0LjIgMC0xOS42bC0zNy40LTM3LjZjLTIuNi0yLjYtNi4xLTQtOS44LTQtMy43IDAtNy4yIDEuNS05LjggNEwyNTYgMTk3LjggMTI0LjkgNjguM2MtMi42LTIuNi02LjEtNC05LjgtNC0zLjcgMC03LjIgMS41LTkuOCA0TDY4IDEwNS45Yy01LjQgNS40LTUuNCAxNC4yIDAgMTkuNmwxMzEuNSAxMzBMNjguNCAzODcuMWMtMi42IDIuNi00LjEgNi4xLTQuMSA5LjggMCAzLjcgMS40IDcuMiA0LjEgOS44bDM3LjQgMzcuNmMyLjcgMi43IDYuMiA0LjEgOS44IDQuMSAzLjUgMCA3LjEtMS4zIDkuOC00LjFMMjU2IDMxMy4xbDEzMC43IDEzMS4xYzIuNyAyLjcgNi4yIDQuMSA5LjggNC4xIDMuNSAwIDcuMS0xLjMgOS44LTQuMWwzNy40LTM3LjZjMi42LTIuNiA0LjEtNi4xIDQuMS05LjgtLjEtMy42LTEuNi03LjEtNC4yLTkuN3oiIGZpbGw9IiNmZmZmZmYiIGNsYXNzPSJmaWxsLTAwMDAwMCI+PC9wYXRoPjwvc3ZnPg==" >
        </div>
        <div class="ppg-loader"></div>
        <iframe class="ppg-iframe" id="ppg-iframe" src="${session.url}" frameborder="0"></iframe>
      </div>
    `;

      body.insertAdjacentHTML('beforeend', modalHtml);

      const iframe = document.getElementById('ppg-iframe');
      const closeBtn = document.getElementById('ppg-btn-close');

      if (iframe) {
        iframe.onload = function () {
          const loader = document.querySelector('.ppg-loader');
          if (loader) loader.remove();
          if (closeBtn) closeBtn.classList.add('ppg-show');
        };
      }

      if (closeBtn) {
        //@ts-expect-error zain ab ain
        closeBtn.onclick = hideCheckout;
      }
    },
    [billingCycle]
  );

  const hideCheckout = useCallback(async (event?: React.MouseEvent) => {
    if (event) event.preventDefault();
    const modal = document.getElementById('ppg-checkout-modal');
    if (modal) modal.remove();

    await userStore.getLatestUser();

    const previous = previousSubscriptionRef.current;
    const current = {
      isActive: userStore.isSubscriptionActive,
      planId: userStore.userSubscription.planId,
      topupCredits: userStore.userSubscription.topupCredits || 0,
    };

    // Check if purchase happened
    const didSubscriptionPurchase =
      (!previous.isActive && current.isActive) || // Free to paid
      (previous.isActive &&
        current.isActive &&
        previous.planId !== current.planId); // Plan upgrade/change

    const didTopupPurchase = current.topupCredits > previous.topupCredits; // Topup credit purchase

    const didPurchase = didSubscriptionPurchase || didTopupPurchase;

    if (didPurchase && purchasedPlanRef.current) {
      const plan = purchasedPlanRef.current;
      // Pass plan info via query params
      const params = new URLSearchParams({
        planId: plan._id,
        planName: plan.name,
        credits: plan.credits.toString(),
        billingCycle: plan.billingCycle,
        isTopup: didTopupPurchase.toString(),
      });
      navigate(`/thank-you?${params.toString()}`);
    }
  }, []);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = checkoutStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  if (loadingState === LoadingState.Loading) {
    return <Skeleton active={true} />;
  }

  return (
    <div className="billing-page">
      <Helmet>
        <title>Billing | ShortsNinja</title>
        <meta
          name="description"
          content="Manage your ShortsNinja subscription and billing details. Update your AI video automation plan."
        />
        <meta
          name="keywords"
          content="billing settings, subscription, ShortsNinja, faceless videos, ai video automation"
        />
      </Helmet>
      <div className="page-header">
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              {t('nav.credits')}
            </Title>
            <Text type="secondary" style={{ fontSize: '15px' }}>
              {t('billing.subscription.title')}
            </Text>
          </div>
        </Flex>
      </div>

      {/* Top Section - Coupon & Subscription Info */}
      <Row gutter={[24, 24]} style={{ marginBottom: '40px' }}>
        <Col xs={24} lg={16}>
          <SubscriptionInfo />
        </Col>
        <Col xs={24} lg={8}>
          <CouponDisplay />
        </Col>
      </Row>

      {/* Tabs for Subscription and Topup Credits */}
      <Tabs
        defaultActiveKey="subscription"
        size="large"
        style={{ marginBottom: '40px' }}
        items={[
          {
            key: 'subscription',
            label: (
              <span style={{ fontSize: '16px', fontWeight: 500 }}>
                <RocketOutlined style={{ marginRight: '8px' }} />
                {t('billing.plans.subscriptionPlans')}
              </span>
            ),
            children: (
              <>
                {/* Pricing Section */}
                <div className="pricing-header">
                  <Title level={2} style={{ margin: 0, marginBottom: '8px' }}>
                    {t('billing.plans.title')}
                  </Title>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: '16px',
                      display: 'block',
                      marginBottom: '24px',
                    }}
                  >
                    {t('billing.subscription.title')}
                  </Text>

                  <div className="billing-cycle-toggle">
                    <Space align="center" size="middle">
                      <Text strong>{t('billing.plans.monthly')}</Text>
                      <Switch
                        size="default"
                        checked={billingCycle === BillingCycle.Annual}
                        onChange={(value: boolean) => {
                          setBillingCycle(
                            value ? BillingCycle.Annual : BillingCycle.Monthly
                          );
                        }}
                      />
                      <Space size={4}>
                        <Text strong>{t('billing.plans.annual')}</Text>
                      </Space>
                    </Space>
                  </div>
                </div>

                {/* Pricing Cards */}
                <Row gutter={[24, 24]} style={{ marginBottom: '60px' }}>
                  {subscriptionPlans.map((plan, index) => {
                    // PayPro-locked users are treated as unsubscribed on the
                    // billing page: no current-plan highlight, no change-plan
                    // modal, plan cards always open fresh Polar checkout.
                    const isUserHasActivePlan =
                      userStore.userSubscription.status ===
                        SubscriptionStatusName.Active &&
                      !userStore.isPayproLocked;
                    const isActivePlan =
                      isUserHasActivePlan &&
                      userStore.userSubscription.planId === plan._id;

                    const isPopular = index === popularPlanIndex;
                    const planStyle = getPlanStyle(
                      plan.name,
                      index,
                      subscriptionPlans.length
                    );

                    const handlePayment = (e) => {
                      // PayPro-locked users always go through the fresh
                      // checkout flow — the change-plan modal would route
                      // to the dead PayPro adapter.
                      if (isUserHasActivePlan && !userStore.isPayproLocked) {
                        planChangeModalRef.current?.open(plan._id);
                        return;
                      }

                      showCheckout(e, plan);

                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-expect-error zubi
                      fbq('track', 'InitiateCheckout', {
                        value: plan.price,
                        currency: 'USD',
                        contents: [
                          {
                            id: plan.name,
                            quantity: 1,
                            item_price: plan.price,
                          },
                        ],
                        content_type: 'product',
                      });
                    };

                    const featureMap = t('billing.featureMap', {
                      returnObjects: true,
                    }) as Record<string, string>;
                    const planNameMap = t('billing.planNameMap', {
                      returnObjects: true,
                    }) as Record<string, string>;

                    return (
                      <Col xs={24} sm={12} lg={6} key={plan._id}>
                        <Badge.Ribbon
                          text={t('billing.plans.mostPopular')}
                          color={planStyle.color}
                          style={{ display: isPopular ? 'block' : 'none' }}
                        >
                          <Card
                            className={`pricing-card ${
                              isActivePlan ? 'active-plan' : ''
                            } ${isPopular ? 'popular-plan' : ''}`}
                            hoverable={!isActivePlan}
                          >
                            {/* Header */}
                            <div className="pricing-card-header">
                              <div
                                className="plan-icon"
                                style={{ background: planStyle.color }}
                              >
                                {planStyle.icon}
                              </div>
                              <Title
                                level={3}
                                style={{ margin: '12px 0 4px 0' }}
                              >
                                {planNameMap[plan.name] ?? plan.name}
                              </Title>
                              <Text
                                type="secondary"
                                style={{ fontSize: '13px' }}
                              >
                                {t('billing.plans.videosPerMonth', {
                                  count: plan.videosPerMonth,
                                })}
                              </Text>
                            </div>

                            {/* Price */}
                            <div className="pricing-card-price">
                              {plan.originalPrice &&
                                plan.originalPrice !== plan.price && (
                                  <div className="original-price">
                                    ${plan.originalPrice}
                                  </div>
                                )}
                              <div className="current-price">
                                <span className="price-amount">
                                  ${plan.price}
                                </span>
                                <span className="price-period">
                                  {billingCycle === BillingCycle.OneTime
                                    ? ''
                                    : billingCycle === BillingCycle.Annual
                                    ? t('billing.plans.perYear')
                                    : t('billing.plans.perMonth')}
                                </span>
                              </div>
                            </div>

                            <Divider style={{ margin: '20px 0' }} />

                            {/* Features */}
                            <div className="pricing-features">
                              <div className="feature-item highlight">
                                <CrownOutlined
                                  style={{ color: planStyle.color }}
                                />
                                <Text strong>
                                  {plan.credits} {t('billing.credits')}
                                </Text>
                              </div>
                              <div className="feature-item highlight">
                                <RocketOutlined
                                  style={{ color: planStyle.color }}
                                />
                                <Text strong>
                                  {plan.series === 0
                                    ? t('billing.plans.noSeries')
                                    : t('billing.plans.seriesCount', {
                                        count: plan.series,
                                      })}
                                </Text>
                              </div>

                              {plan.features.map((feature, featureIndex) => (
                                <div
                                  key={featureIndex}
                                  className="feature-item"
                                >
                                  <CheckOutlined />
                                  <Text>{featureMap[feature] ?? feature}</Text>
                                </div>
                              ))}
                            </div>

                            {/* CTA Button */}
                            <Button
                              type="primary"
                              size="large"
                              block
                              disabled={isActivePlan}
                              onClick={handlePayment}
                              className="pricing-cta"
                              style={{
                                background: isActivePlan
                                  ? undefined
                                  : planStyle.color,
                                borderColor: isActivePlan
                                  ? undefined
                                  : planStyle.color,
                              }}
                            >
                              {isActivePlan
                                ? t('billing.plans.currentPlan')
                                : isUserHasActivePlan
                                ? `${t('billing.actions.downgrade')} ${
                                    plan.name
                                  }`
                                : t('billing.plans.selectPlan')}
                            </Button>
                          </Card>
                        </Badge.Ribbon>
                      </Col>
                    );
                  })}
                </Row>
              </>
            ),
          },
          {
            key: 'topup',
            label: (
              <span style={{ fontSize: '16px', fontWeight: 500 }}>
                <ThunderboltOutlined style={{ marginRight: '8px' }} />
                {t('billing.plans.topupTab')}
              </span>
            ),
            children: (
              <>
                {/* Topup Credits Section */}
                <div className="pricing-header">
                  <Title level={2} style={{ margin: 0, marginBottom: '8px' }}>
                    {t('billing.topup.title')}
                  </Title>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: '16px',
                      display: 'block',
                      marginBottom: '24px',
                    }}
                  >
                    {t('billing.topup.description')}
                  </Text>
                </div>

                {/* Topup Credit Packages */}
                <Row gutter={[24, 24]} style={{ marginBottom: '60px' }}>
                  {topupPlans.map((plan, index) => {
                    const planStyle = getPlanStyle(
                      plan.name,
                      index,
                      topupPlans.length
                    );
                    const isPopular = plan.isMostPopular === true;
                    const planNameMap = t('billing.planNameMap', {
                      returnObjects: true,
                    }) as Record<string, string>;

                    return (
                      <Col xs={24} sm={12} lg={6} key={plan._id}>
                        <Badge.Ribbon
                          text={t('billing.plans.mostPopular')}
                          color={planStyle.color}
                          style={{ display: isPopular ? 'block' : 'none' }}
                        >
                          <Card
                            className={`pricing-card ${
                              isPopular ? 'popular-plan' : ''
                            }`}
                            hoverable
                          >
                            {/* Header */}
                            <div className="pricing-card-header">
                              <div
                                className="plan-icon"
                                style={{ background: planStyle.color }}
                              >
                                {planStyle.icon}
                              </div>
                              <Title
                                level={3}
                                style={{ margin: '12px 0 4px 0' }}
                              >
                                {planNameMap[plan.name] ?? plan.name}
                              </Title>
                              <Text
                                type="secondary"
                                style={{ fontSize: '13px' }}
                              >
                                {t('billing.topup.oneTimePurchase')}
                              </Text>
                            </div>

                            {/* Price */}
                            <div className="pricing-card-price">
                              {plan.originalPrice &&
                                plan.originalPrice !== plan.price && (
                                  <div className="original-price">
                                    ${plan.originalPrice}
                                  </div>
                                )}
                              <div className="current-price">
                                <span className="price-amount">
                                  ${plan.price}
                                </span>
                              </div>
                            </div>

                            <Divider style={{ margin: '20px 0' }} />

                            {/* Features */}
                            <div className="pricing-features">
                              <div className="feature-item highlight">
                                <ThunderboltOutlined
                                  style={{ color: planStyle.color }}
                                />
                                <Text strong>
                                  {plan.credits} {t('billing.credits')}
                                </Text>
                              </div>
                              <div className="feature-item">
                                <CheckOutlined />
                                <Text>{t('billing.topup.neverExpires')}</Text>
                              </div>
                              <div className="feature-item">
                                <CheckOutlined />
                                <Text>{t('billing.topup.useAnytime')}</Text>
                              </div>
                              <div className="feature-item">
                                <CheckOutlined />
                                <Text>
                                  {t('billing.topup.stacksWithSubscription')}
                                </Text>
                              </div>
                            </div>

                            {/* CTA Button */}
                            <Button
                              type="primary"
                              size="large"
                              block
                              onClick={(e) => showCheckout(e, plan)}
                              className="pricing-cta"
                              style={{
                                background: planStyle.color,
                                borderColor: planStyle.color,
                              }}
                            >
                              {t('billing.plans.selectPlan')}
                            </Button>
                          </Card>
                        </Badge.Ribbon>
                      </Col>
                    );
                  })}
                </Row>
              </>
            ),
          },
        ]}
      />

      <Faq />
      <PlanChangeModal ref={planChangeModalRef} />
    </div>
  );
};

export default observer(BillingPage);
